import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { Check, ArrowRight, Loader2, Zap } from "lucide-react";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    emoji: "🌱",
    bg: "callout-gray",
    tagline: "Unlimited scans for signed-up users.",
    features: [
      "Unlimited SEO + GEO scans",
      "Full AI action plan (all priorities)",
      "Complete scan history",
      "Copy & download reports",
      "Keyword and GEO tactic suggestions",
    ],
    cta: "You're on Free",
    ctaDisabled: true,
  },
  {
    id: "pro_monthly",
    name: "Pro",
    price: "$9",
    period: "/month",
    emoji: "🚀",
    bg: "callout-blue",
    featured: true,
    tagline: "For serious optimizers.",
    features: [
      "Everything in Free",
      "Priority AI processing",
      "PDF export with your branding (coming soon)",
      "Bulk URL scanning (coming soon)",
      "Priority email support",
      "Early access to new features",
    ],
    cta: "Upgrade to Pro",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const authed = user && user !== false;
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const upgrade = async (planId) => {
    setError("");
    if (!authed) {
      navigate("/signup");
      return;
    }
    setBusy(planId);
    trackEvent("upgrade_clicked", { plan_id: planId });
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(null);
    }
  };

  const currentPlan = authed && user.plan === "pro" ? "pro_monthly" : "free";

  return (
    <div className="bg-white text-[#37352F] min-h-[calc(100vh-3.5rem)]">
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full callout-gray text-xs text-[#787774] mb-6">
            <Zap className="w-3.5 h-3.5" />
            <span>Simple, honest pricing</span>
          </div>
          <h1 className="font-serif-h text-4xl md:text-6xl font-semibold tracking-tight mb-4">Free to start. Pro when you&apos;re ready.</h1>
          <p className="text-[#787774] text-lg max-w-2xl mx-auto">Guests get 3 free scans. Signed-up users get unlimited scans. Upgrade to Pro for advanced features.</p>
        </div>

        {error && (
          <div className="max-w-md mx-auto callout-red rounded-md text-[#8B2A20] text-sm px-4 py-3 mb-6" data-testid="pricing-error">{error}</div>
        )}

        <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {PLANS.map((p) => {
            const isCurrent = p.id === currentPlan;
            return (
              <div
                key={p.id}
                className={`border rounded-lg p-8 relative ${p.featured ? "border-[#2383E2] shadow-lg" : "border-[#EBEBEA]"}`}
                data-testid={`plan-card-${p.id}`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-6 bg-[#2383E2] text-white text-xs font-semibold px-2.5 py-1 rounded-md">Most popular</span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <span className={`notion-emoji-box ${p.bg}`}>{p.emoji}</span>
                  <h2 className="font-serif-h text-2xl font-semibold">{p.name}</h2>
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-5xl font-bold tracking-tight">{p.price}</span>
                  <span className="text-[#787774]">{p.period}</span>
                </div>
                <p className="text-[#787774] text-sm mb-6">{p.tagline}</p>
                <ul className="space-y-2.5 mb-8">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-sm text-[#37352F]">
                      <Check className="w-4 h-4 text-[#0F7B0F] shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>

                {p.id === "free" ? (
                  <button
                    disabled
                    className="w-full border border-[#EBEBEA] text-[#787774] font-medium py-2.5 rounded-md cursor-default"
                    data-testid="plan-free-btn"
                  >
                    {authed && currentPlan === "free" ? "Your current plan" : "Free forever"}
                  </button>
                ) : isCurrent ? (
                  <button
                    disabled
                    className="w-full bg-[#DDEDDD] text-[#0F7B0F] font-medium py-2.5 rounded-md cursor-default"
                    data-testid="plan-pro-current"
                  >
                    ✓ You&apos;re on Pro
                  </button>
                ) : (
                  <button
                    onClick={() => upgrade(p.id)}
                    disabled={busy === p.id}
                    data-testid="plan-pro-upgrade"
                    className="w-full bg-[#2383E2] text-white font-medium py-2.5 rounded-md flex items-center justify-center gap-2 hover:bg-[#1A6FCC] transition-colors disabled:opacity-60"
                  >
                    {busy === p.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>{authed ? p.cta : "Sign up to upgrade"} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-[#9B9A97] mt-8">Payments processed securely by Stripe · Cancel anytime · No hidden fees</p>
      </div>
    </div>
  );
}
